import type { FC, FormEvent } from 'react'
import { useEffect, useState } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { Country } from '@jotta/grpc-connect-openapi/country'
import { Grid } from '@jotta/ui/Grid'

import { ErrorMessage } from '../../Elements/ErrorMessage/ErrorMessage'
import { CodeInput } from '../../Forms/CodeInput/CodeInput'
import type { FormInputTextProps } from '../../Forms/FormInputText/FormInputText'
import { PhoneInput } from '../../Forms/PhoneInput/PhoneInput'
import type { RadixDialogProps } from '../RadixDialog/RadixDialog'
import { RadixDialog } from '../RadixDialog/RadixDialog'

export interface VerifyPhoneDialogProps extends RadixDialogProps {
  destructive?: boolean
  /** Label text for verify button */
  verifyButtonText: string
  /** Label text for cancel button */
  cancelButtonText: string
  /** Verification handler  */
  onVerify: (phone: string, code?: string) => void
  onClose: (open: boolean) => void
  uiState?:
    | 'VERIFY_PHONE'
    | 'VERIFY_PHONE_PENDING'
    | 'VERIFY_CODE'
    | 'VERIFY_CODE_PENDING'
    | 'VERIFY_SUCCESS'
  phoneNumber?: string
  country?: Country
  code?: string
}
export const VerifyPhoneDialog: FC<VerifyPhoneDialogProps> = ({
  uiState = 'VERIFY_PHONE',
  title,
  smallTitle,
  destructive,
  verifyButtonText,
  errorMessage,
  skipCloseButton,
  onClose,
  onVerify,
  cancelButtonText,
  phoneNumber = '',
  code: initialCode = '',
  country = Country.NORWAY,
}) => {
  const [phone, setPhone] = useState(phoneNumber)
  const [code, setCode] = useState(initialCode)
  const [submitButton, setSubmitButton] = useState<HTMLButtonElement | null>()
  const [codeValidState, setCodeValidState] =
    useState<FormInputTextProps['validState']>('NOT_VALIDATED')
  const [phoneValidState, setPhoneValidState] =
    useState<FormInputTextProps['validState']>('NOT_VALIDATED')
  const handlePhoneChange = (value: string) => {
    setPhone(value)
  }

  useEffect(() => {
    if (!phone.length) {
      setPhoneValidState('NOT_VALID')
    } else {
      setPhoneValidState('VALID')
    }

    if (code.length === 6) {
      setCodeValidState('VALID')
    } else {
      setCodeValidState('NOT_VALID')
    }
  }, [phone, code, onVerify])

  const showSpinner =
    uiState === 'VERIFY_CODE_PENDING' || uiState === 'VERIFY_PHONE_PENDING'
  const showPhoneInput = uiState.startsWith('VERIFY_PHONE')
  const showCodeInput = uiState.startsWith('VERIFY_CODE')
  const showSuccess = uiState === 'VERIFY_SUCCESS'
  const verifyButtonDisabled =
    (showPhoneInput && phoneValidState !== 'VALID') ||
    (showCodeInput && codeValidState !== 'VALID')
  const handleVerify = (e?: FormEvent) => {
    e?.preventDefault()
    if (verifyButtonDisabled) {
      return
    }
    onVerify(phone, code)
  }

  return (
    <RadixDialog
      skipCloseButton={skipCloseButton}
      onClose={onClose}
      title={title}
      smallTitle={smallTitle}
      buttons={[
        {
          action: 'close',
          label: cancelButtonText,
          type: 'outline',
        },
        {
          disabled: verifyButtonDisabled,
          label: verifyButtonText,
          loading: showSpinner,
          type: destructive ? 'destructive' : 'primary',
          action: handleVerify,
          ref: setSubmitButton,
        },
      ]}
    >
      <Grid columns={[1]} gap={4} as="form" onSubmit={handleVerify}>
        <ErrorMessage error={errorMessage} />
        {showPhoneInput && (
          <PhoneInput
            autoFocus
            country={country}
            value={phone}
            onChange={event => handlePhoneChange(event.target.value)}
            placeholder={t`Please enter phone number`}
          />
        )}
        {showCodeInput && (
          <div>
            <p>
              <Trans>We need to verify that this is your phone number.</Trans>
              &nbsp;
              <Trans>
                An SMS with a verification code was just sent to your phone.
              </Trans>
            </p>
            <CodeInput
              autoFocus
              className="my-12 justify-center"
              inputMode="numeric"
              groupLength={3}
              codeLength={6}
              code={code}
              onCodeChange={setCode}
              onCodeComplete={() => submitButton?.focus()}
            />
          </div>
        )}
        {showSuccess && <p>Success!</p>}
      </Grid>
    </RadixDialog>
  )
}
